import React, { Component, useRef, useEffect, useState } from "react";

import { connect } from "react-redux";
import { withNamespaces } from "react-i18next";
import { Button, Input, Table, Select, message } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import ProductsSelectWithFilter from "@/components/common/filters/ProductsSelectWithFilter";
import instance from "@/store/actions/instance";
import { getDefaultUnit } from "../newPurchaseOrderModal/AddPurchaseProductModal";
import { setProductsSuppliers, setProductsUnits } from "@/store/actions";
import InventoryExcelUploader from "@/components/StockUpdateOrders/StockUpdateOrderDetailsModal/InventoryExcelUploader";

const { Column } = Table;
const { Option } = Select;

const EditableContext = React.createContext();

const EditableRow = ({ index, ...props }) => {
  return (
    <EditableContext.Provider>
      <tr {...props} />
    </EditableContext.Provider>
  );
};

const EditableCell = ({
  editable,
  children,
  record,
  handleSave,
  direction,
  value,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef();
  useEffect(() => {
    if (editing) {
      inputRef.current.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
  };

  const save = (e) => {
    toggleEdit();
    if (e.target.value && e.target.value !== value) handleSave(e.target.value);
  };

  if (editable) {
    children = editing ? (
      <Input
        defaultValue={value}
        ref={inputRef}
        onPressEnter={save}
        onBlur={save}
        style={{ direction: direction }}
        min={0}
      />
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{
          paddingRight: 24,
        }}
        onClick={toggleEdit}
      >
        {value}
      </div>
    );
  }

  return <td {...restProps}>{children}</td>;
};

class PurchaseOrderProductsTable extends Component {
  state = { loading: false, excelModalOpen: false };
  currency =
    this.props.config.language == "arabic"
      ? this.props.settings.currency_local
      : this.props.settings.currency_english;

  addProduct = (product) => {
    const { units, allProducts, selectedOrder } = this.props;
    const selectedProduct = allProducts.find(
      (p) => p.id === product.product_id,
    );
    if (!selectedProduct) return null;
    let productUnits =
      selectedProduct?.units?.filter((u) => !u.is_deleted) || [];

    let productSuppliers =
      selectedProduct?.suppliers?.filter((s) => !s.is_deleted) || [];

    const productSupplier = productSuppliers.find(
      (s) => s.supplier_id === selectedOrder.supplier_id,
    );

    let baseUnit = units.find((u) => u.id === selectedProduct.unit);
    let unit = productUnits.find((u) => u.unit_id === product.unit_id);
    baseUnit = {
      ...baseUnit,
      unit__name: baseUnit?.name,
      unit__ar_name: baseUnit?.ar_name,
      conversion_factor: 1,
      unit_id: baseUnit?.id || null,
    };
    return {
      product__name: selectedProduct.name,
      product__ar_name: selectedProduct.ar_name,
      product__sku: selectedProduct.sku,
      product_id: selectedProduct.id,
      id: selectedProduct.id,
      key: selectedProduct.id,
      cost_per_unit: productSupplier?.price || product.cost_per_unit,
      quantity: product.quantity,
      received_quantity: 0,
      supplier_code: product.supplier_code || productSupplier?.code,
      baseUnit: baseUnit,
      conversion_factor: unit?.conversion_factor || 1,
      unit:
        unit ||
        getDefaultUnit(productUnits || [], baseUnit, "default_for_purchase"),
      units: productUnits || [],
      type: "new-create",
    };
  };
  fetchProductsUnits = async (productsIds) => {
    const { t } = this.props;
    try {
      this.setState({ loading: true });
      const res = await instance.post(`/get_products_units/`, { productsIds });
      this.props.setProductsUnits(res.data);
      this.setState({ loading: false });
      return res.data;
    } catch (err) {
      if (err.response && err.response.status == 403)
        message.error(t("AccessDeniedMessage"));
      else message.error(t("SomethingWentWrong"));
    }
    this.setState({ loading: false });
  };
  fetchProductsSuppliers = async (productsIds) => {
    const { t } = this.props;
    try {
      this.setState({ loading: true });
      const res = await instance.post(`/get_products_suppliers/`, {
        productsIds: productsIds,
      });
      this.props.setProductsSuppliers(res.data);
      this.setState({ loading: false });
      return res.data;
    } catch (err) {
      console.log(err);
      if (err.response && err.response.status == 403)
        message.error(t("AccessDeniedMessage"));
      else message.error(t("SomethingWentWrong"));
    }
    this.setState({ loading: false });
  };
  renderColumnProduct = (productId, record, index) => {
    const { t, config, products } = this.props;
    if (record.type !== "New") {
      return (
        <span style={{ whiteSpace: "break-spaces" }}>
          {`${
            (config.language === "arabic" && record.product__ar_name) ||
            record.product__name
          } ${record.product__sku} `}
        </span>
      );
    }
    const currentProductsIds = products
      .filter((p) => !p.is_deleted)
      .map((p) => p.product_id);

    return (
      <ProductsSelectWithFilter
        loading={this.state.loading}
        onSelect={async (newSelectedProduct) => {
          const selectedProduct = this.props.allProducts.find(
            (p) => p.id === newSelectedProduct,
          );
          const newProducts = [...this.props.products];
          if (selectedProduct.units == null) {
            await this.fetchProductsUnits([newSelectedProduct]);
          }
          if (selectedProduct.suppliers == null) {
            await this.fetchProductsSuppliers([newSelectedProduct]);
          }
          const product = this.addProduct({
            product_id: selectedProduct.id,
            unit_id: selectedProduct.unit_id,
            cost_per_unit: selectedProduct.suppliers_price || 0,
            quantity: 1,
            supplier_code: "",
          });
          if (product) {
            newProducts.push(product);
          }
          this.props.setProducts(newProducts);
        }}
        customFilter={(p) =>
          p.type_of_product === "stocked" &&
          !p.is_deleted &&
          !currentProductsIds.includes(p.id)
        }
      />
    );
  };
  renderColumnUnit = (unit, record, index) => {
    const { t, config, editMode } = this.props;
    if (!editMode)
      return (
        <span>
          {(config.language === "arabic" && record.unit__ar_name) ||
            record.unit__name ||
            "-"}
        </span>
      );
    let productUnits = [];
    if (record.baseUnit?.id) {
      productUnits.push(
        <Option key={record.baseUnit?.id} value={record.baseUnit?.id}>
          {(config.language === "arabic" && record.baseUnit.ar_name) ||
            record.baseUnit.name}
        </Option>,
      );
    }
    record.units?.forEach((unit) => {
      const unitName =
        config.language == "arabic"
          ? unit.unit__ar_name || unit.unit__name
          : unit.unit__name;

      productUnits.push(
        <Option value={unit.unit_id} key={unit.unit_id}>
          {unitName}
        </Option>,
      );
    });

    return productUnits.length > 1 ? (
      <Select
        style={{ width: "100%" }}
        placeholder={t("select a unit ")}
        defaultValue={unit.unit_id}
        onSelect={(unitId) => {
          const newProducts = [...this.props.products];
          newProducts[index].unit =
            record.units.find((u) => u.unit_id === unitId) || record.baseUnit;
          newProducts[index].unit__name = newProducts[index].unit.unit__name;
          newProducts[index].unit__ar_name =
            newProducts[index].unit.unit__ar_name;
          this.props.setProducts(newProducts);
        }}
      >
        {productUnits}
      </Select>
    ) : (
      <span>
        {(config.language === "arabic" &&
          (record.units?.[0] || record.baseUnit)?.unit__ar_name) ||
          (record.units?.[0] || record.baseUnit)?.unit__name ||
          "-"}
      </span>
    );
  };
  renderColumnDelete = (text, record, index) => {
    return (
      <Button
        disabled={record.type == "New"}
        onClick={() => {
          const newProducts = [...this.props.products];
          if (newProducts[index].type === "new-create") {
            newProducts.splice(index, 1);
          } else {
            newProducts[index].is_deleted = true;
          }
          this.props.setProducts(newProducts);
        }}
        danger
        shape="circle"
        icon={<DeleteOutlined style={{ top: 0 }} />}
      ></Button>
    );
  };
  render() {
    const {
      t,
      products,
      selectedOrder,
      settings,
      config,
      editMode,
      permissions,
      isViewOnly,
      isReceiving,
    } = this.props;

    const { excelModalOpen } = this.state;

    return (
      <div>
        {excelModalOpen && (
          <InventoryExcelUploader
            visible={excelModalOpen}
            selectedOrder={selectedOrder}
            type={"purchase_order"}
            onClose={async (shouldRefetch, newTotal) => {
              this.setState({ excelModalOpen: false });
              if (shouldRefetch) {
                selectedOrder.total = newTotal;
                await this.props.fetchPurchaseOrderDetails();
              }
            }}
          />
        )}
        {!editMode &&
          !isReceiving &&
          permissions.can_update_purchase_orders &&
          ["Draft"].includes(selectedOrder.status) &&
          !selectedOrder.email_sent &&
          !isViewOnly && (
            <div
              style={{
                display: "flex",
                gap: 16,
                float: (config.language === "arabic" && "left") || "right",
              }}
            >
              <Button
                icon={<EditOutlined />}
                onClick={() => this.props.setEditMode(true)}
                className="my-2"
                style={{
                  float: (config.language === "arabic" && "left") || "right",
                }}
              >
                {t("Add / Edit")}
              </Button>
              <Button
                icon={
                  <DownloadOutlined style={{ position: "relative", top: -2 }} />
                }
                onClick={() => {
                  this.setState({ excelModalOpen: true });
                }}
                className="my-2"
              >
                {t("Import")}
              </Button>
            </div>
          )}
        <Table
          dataSource={
            editMode ? [...products, { type: "New", units: [] }] : products
          }
          pagination={false}
          rowClassName={(record, index) =>
            record.is_deleted ? "d-none" : "text-center editable-row"
          }
          components={{
            body: {
              row: EditableRow,
              cell: EditableCell,
            },
          }}
          className="my-3"
          footer={
            !editMode
              ? () => (
                  <>
                    {selectedOrder.service_cost > 0 && (
                      <div
                        className="mb-2"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <span>{t("Service Cost")}</span>
                        <span>
                          {`${selectedOrder.service_cost.toFixed(
                            settings.currency_decimals,
                          )} ${this.currency}`}
                        </span>
                      </div>
                    )}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span style={{ fontWeight: "bold" }}>
                        {t("Total Quoted")}
                      </span>
                      <span>
                        {`${selectedOrder.total.toFixed(
                          settings.currency_decimals,
                        )} ${this.currency}`}
                      </span>
                    </div>
                    <div
                      className="mt-2"
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span style={{ fontWeight: "bold" }}>
                        {t("Total Received")}
                      </span>
                      <span>
                        {`${selectedOrder.received_total?.toFixed(
                          settings.currency_decimals,
                        )} ${this.currency}`}
                      </span>
                    </div>
                  </>
                )
              : null
          }
        >
          <Column
            title={t("Product")}
            key="product_id"
            dataIndex={"product_id"}
            render={(productId, record, index) =>
              this.renderColumnProduct(productId, record, index)
            }
          />

          <Column
            title={t("Supplier's Code")}
            dataIndex="supplier_code"
            key="supplier_code"
            render={(text) => text || "-"}
            onCell={(record, index) => ({
              record,
              editable: editMode,
              value: record.supplier_code,
              handleSave: (newSupplierCode) => {
                const newProducts = [...this.props.products];
                newProducts[index].supplier_code = newSupplierCode;
                this.props.setProducts(newProducts);
              },
            })}
          />
          <Column
            title={t("Unit")}
            dataIndex="unit"
            key="unit"
            render={(unit, record, index) =>
              this.renderColumnUnit(unit, record, index)
            }
          />
          <Column
            title={t("Cost")}
            dataIndex="cost_per_unit"
            key="cost"
            onCell={(record, index) => ({
              record,
              editable: editMode,
              value: record.cost_per_unit,
              handleSave: (newCost) => {
                if (newCost >= 0 && !Number.isNaN(newCost)) {
                  const newProducts = [...this.props.products];
                  newProducts[index].cost_per_unit = Number(newCost);
                  this.props.setProducts(newProducts);
                }
              },
            })}
            render={(text, record) =>
              record.type !== "New" &&
              `${text.toFixed(settings.currency_decimals)} ${this.currency}`
            }
          />
          <Column
            title={t("Quantity")}
            dataIndex="quantity"
            key="quantity"
            onCell={(record, index) => ({
              record,
              editable: editMode,
              value: record.quantity,
              handleSave: (newQuantity) => {
                if (newQuantity >= 0 && !Number.isNaN(newQuantity)) {
                  const newProducts = [...products];
                  newProducts[index].quantity = Number(newQuantity);
                  this.props.setProducts(newProducts);
                }
              },
            })}
          />
          <Column
            title={t("Received Quantity")}
            dataIndex="received_quantity"
            key="received_quantity"
            onCell={(record, index) => ({
              record,
              editable: isReceiving,
              value:
                isReceiving && Number(record.new_received_quantity)
                  ? record.new_received_quantity
                  : record.received_quantity,
              handleSave: (newQuantity) => {
                if (!Number.isNaN(newQuantity) && parseInt(newQuantity) >= 0) {
                  const newProducts = [...products];
                  newProducts[index].new_received_quantity = Math.min(
                    Math.max(parseInt(newQuantity), record.received_quantity),
                    record.quantity,
                  );
                  this.props.setProducts(newProducts);
                }
              },
            })}
          />

          <Column
            title={t("Total")}
            key="total"
            render={(record) =>
              record.type !== "New" &&
              `${(record.cost_per_unit * record.quantity).toFixed(
                settings.currency_decimals,
              )} ${this.currency}`
            }
          />
          {editMode && (
            <Column
              title={t("Delete")}
              key="delete"
              render={this.renderColumnDelete}
            />
          )}
        </Table>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    permissions: state.auth.permissions,
    settings: state.auth.settings,
    config: state.config,
    allProducts: state.products.allProducts,
    units: state.products.units,

    is_staff: state.auth.is_staff,
  };
};
const mapDispatchToProps = (dispatch) => {
  return {
    setProductsUnits: (payload) => dispatch(setProductsUnits(payload)),
    setProductsSuppliers: (payload) => dispatch(setProductsSuppliers(payload)),
  };
};
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withNamespaces()(PurchaseOrderProductsTable));
